import React from 'react';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@/shared/utils/cn';
import { Icon } from '@/modules/Icon';
import { Badge } from '@/modules/Badge';
import { Button } from '@/modules/Button';
import { SidebarWorkspaceCardProps } from '../../types';
import { SidebarDivider } from '../atoms';
import { IconType, AnyIconDefinition } from '@/shared/types/types';

/**
 * “Mesa de trabajo” surface for the sidebar rail (Figma Sidebar-Menu `_Storage Usage Widget`):
 * gradient fill, dashed border, section label, icon + primary line + badge, divider, text CTAs.
 * Icon + headline sit on one marquee track (vertically centered vs. badge); hover scrolls icon + title together (see `ui-sidebar-workspace-marquee-*` in shared styles).
 */
export const SidebarWorkspaceCard: React.FC<SidebarWorkspaceCardProps> = ({
  icon = faPenToSquare,
  sectionLabel = 'Mesa de trabajo',
  title = '',
  badge,
  projectId,
  showActions = true,
  onOpen,
  onClose,
  closeLabel = 'Cerrar',
  primaryActionLabel = 'Ver pauta',
  cardBorderStyle = 'dashed',
  className,
}) => {
  const headline = projectId ?? title;
  const showCtaRow =
    showActions &&
    ((onClose !== null && onClose !== undefined) || (onOpen !== null && onOpen !== undefined));

  return (
    <div
      className={cn(
        'ui-flex ui-w-full ui-flex-col ui-overflow-hidden ui-pb-[1px]',
        'ui-from-surface-brand-default ui-to-surface-default ui-bg-gradient-to-b',
        'ui-border-1 ui-border-default ui-p-12',
        cardBorderStyle === 'dashed' ? 'ui-border-dashed' : 'ui-border-solid',
        'ui-rounded-surface-card ui-transition-colors',
        className
      )}
    >
      <p
        className={cn(
          'ui-m-0 ui-w-full ui-shrink-0',
          'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
          'ui-text-tertiary'
        )}
      >
        {sectionLabel}
      </p>

      <div className="ui-flex ui-w-full ui-items-center ui-gap-8 ui-py-8">
        <div className="ui-flex ui-min-h-[var(--ui-type-body-lg-Regular-ih)] ui-min-w-0 ui-flex-1 ui-items-center">
          <div className="ui-sidebar-workspace-marquee-clip ui-w-full ui-min-w-0">
            <div className="ui-sidebar-workspace-marquee-track ui-inline-flex ui-items-center ui-gap-8">
              <span
                className="ui-inline-flex ui-shrink-0 ui-items-center ui-justify-center"
                aria-hidden
              >
                <Icon name={icon as IconType | AnyIconDefinition} size="sm" color="brand" />
              </span>
              {headline ? (
                <span
                  className={cn(
                    'ui-whitespace-nowrap',
                    'ui-text-body-lg-medium ui-font-body-lg-medium ui-leading-body-lg-regular ui-tracking-body-lg',
                    'ui-text-brand'
                  )}
                >
                  {headline}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        {badge ? (
          <div className="ui-inline-flex ui-shrink-0 ui-items-center ui-justify-center">
            <Badge
              label={String(badge.label)}
              color={badge.color || 'accent'}
              theme={badge.theme || 'solid'}
              size="sm"
            />
          </div>
        ) : null}
      </div>

      <SidebarDivider />

      {showCtaRow ? (
        <div className="ui-flex ui-w-full ui-items-stretch ui-gap-8 ui-py-8">
          {onClose !== null && onClose !== undefined ? (
            <Button
              type="button"
              variant="text"
              color="destructive"
              size="xxs"
              fullWidth
              onClick={onClose}
            >
              {closeLabel}
            </Button>
          ) : null}
          {onOpen !== null && onOpen !== undefined ? (
            <Button
              type="button"
              variant="text"
              color="secondary"
              size="xxs"
              fullWidth
              onClick={onOpen}
            >
              {primaryActionLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
